import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode } from "vue"

import { ref, watch, onMounted } from 'vue';
import { useProductsStore } from '@/store/products';
import { useClientsStore } from '@/store/clients';
import { debounce } from 'lodash';


export default /*@__PURE__*/_defineComponent({
  __name: 'ProductSelector',
  props: {
  modelValue: { type: Object, required: true },
  clientId: { type: [String, Number], required: false },
  subjectId: { type: [String, Number], required: false }
},
  emits: ['update:modelValue', 'validation'],
  setup(__props, { emit: __emit }) {

const cachedSearches = new Map<string, any[]>();

const emit = __emit;
const props = __props;

const clientStore = useClientsStore();
const productsStore = useProductsStore();

const localValue = ref({ ...props.modelValue });
const productOptions = ref<any[]>([]);
const productableOptions = ref<any[]>([]);
const loadingProducts = ref(false);

const validate = () => {
  const valid =
    !!localValue.value.item_id &&
    (!!localValue.value.unit_price || localValue.value.unit_price === 0) &&
    (productableOptions.value.length === 0 || !!localValue.value.productable_id);

  emit('validation', valid);
};

watch(() => props.modelValue, (val) => {
  localValue.value = { ...val };

  if (val.productables && val.productables.length) {
    productableOptions.value = val.productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
  }
}, { deep: true });

watch(() => localValue.value, (val) => {
  emit('update:modelValue', val);
  validate();
}, { deep: true });

const onProductSelect = async (productId: number) => {
  const selected = productOptions.value.find(p => p.value === productId);
  if (!selected) return;

  localValue.value.item_id = productId;

  const productables = selected.raw?.attributes?.productable || [];

  if (productables.length > 0) {
    productableOptions.value = productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
    localValue.value.productable_id = null;
    localValue.value.productables = productables;
  } else {
    await fetchPrice({
      productId,
      productType: selected.raw?.attributes?.productable_type
    });
    productableOptions.value = [];
    localValue.value.productable_id = productId;
    localValue.value.productable_type = selected.raw?.attributes?.productable_type;
  }
};

const onProductableSelect = async (productableId: number) => {
  const product = localValue.value.productables?.find((p: any) => p.id === productableId);
  if (!product) return;

  localValue.value.productable_id = productableId;
  localValue.value.productable_type = product.attributes.productable_type;

  await fetchPrice({
    productId: productableId,
    productType: product.attributes.productable_type,
  });
};

const fetchPrice = async ({
  productId,
  productType
}: {
  productId: number;
  productType: string;
}) => {
  const subjectId = props.subjectId;
  const subject = clientStore.clients_leads.find(c => c.id === subjectId);
  const clientTypeId = subject?.attributes?.client_type?.id;

  if (!subjectId || !clientTypeId || !productId || !productType) return;

  try {
    const price = await productsStore.fetchSuggestedPrice('filter[client_type_id]=' + clientTypeId + '&filter[product_type]=' + productType + '&filter[product_id]=' + productId);
    localValue.value.unit_price = price;
  } catch (err) {
    console.error('Error fetching price:', err);
  }
};

const onSearch = debounce(async (search: string) => {
  if (!search || search.length < 2) return;
  loadingProducts.value = true;

  if (cachedSearches.has(search)) {
    productOptions.value = cachedSearches.get(search)!;
    loadingProducts.value = false;
    return;
  }

  try {
    const { data } = await productsStore.fetchProducts({ 'filter[description]': search });
    const mapped = mapProducts(data);
    cachedSearches.set(search, mapped);
    productOptions.value = mapped;
  } catch (error) {
    console.error('Error searching products:', error);
  } finally {
    loadingProducts.value = false;
  }
}, 300);

const mapProducts = (data: any[]) =>
  data.map(p => ({
    value: p.id,
    label: p.attributes.description,
    raw: p
  }));

onMounted(() => {
  const { item_id, productables, productable_type, product_description } = localValue.value;

  // Si es productable y tiene product base
  if (productable_type !== 'Product' && productables?.length) {
    const product = productables[0]?.attributes?.product;

    if (product) {
      productOptions.value = [{
        value: item_id,
        label: product.attributes?.description || 'Producto',
        raw: product
      }];
    }

    productableOptions.value = productables.map((p: any) => ({
      value: p.id,
      label: `${p.attributes.sku || ''} | ${p.attributes.address || ''}`.trim(),
      raw: p
    }));
  }

  // Si es producto directo
  if (productable_type === 'Product' && item_id) {
    productOptions.value = [{
      value: item_id,
      label: product_description,
      raw: { id: item_id }
    }];
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_row = _resolveComponent("v-row")!

  return (localValue.value)
    ? (_openBlock(), _createBlock(_component_v_row, {
        key: 0,
        class: "ma-0",
        dense: ""
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_col, {
            cols: productableOptions.value.length ? 4 : 9,
            class: "py-0"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_autocomplete, {
                modelValue: localValue.value.item_id,
                "onUpdate:modelValue": [
                  _cache[0] || (_cache[0] = ($event: any) => ((localValue.value.item_id) = $event)),
                  onProductSelect
                ],
                items: productOptions.value,
                loading: loadingProducts.value,
                "item-title": "label",
                "item-value": "value",
                label: "Producto",
                density: "compact",
                "onUpdate:search": _unref(onSearch)
              }, null, 8, ["modelValue", "items", "loading", "onUpdate:search"])
            ]),
            _: 1
          }, 8, ["cols"]),
          (productableOptions.value.length)
            ? (_openBlock(), _createBlock(_component_v_col, {
                key: 0,
                cols: 5,
                class: "py-0"
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_v_autocomplete, {
                    modelValue: localValue.value.productable_id,
                    "onUpdate:modelValue": [
                      _cache[1] || (_cache[1] = ($event: any) => ((localValue.value.productable_id) = $event)),
                      onProductableSelect
                    ],
                    items: productableOptions.value,
                    "item-title": "label",
                    "item-value": "value",
                    label: "Seleccionar",
                    density: "compact"
                  }, null, 8, ["modelValue", "items"])
                ]),
                _: 1
              }))
            : _createCommentVNode("", true),
          _createVNode(_component_v_col, {
            cols: 3,
            class: "py-0"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                modelValue: localValue.value.unit_price,
                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((localValue.value.unit_price) = $event)),
                prefix: "$",
                type: "number",
                label: "Precio",
                density: "compact"
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})